/*  _| |____________________________________________| |__
* (__   ____________________________________________   __)
*    | |                                            | |
*    | |                  WARNING                   | |
*    | | This file's code is literally copied in    | |
*    | | ABBA UI's app/javascript/rules/eval.js     | |
*    | | Any updates need to be copied there too!   | |
*    | |                                            | |
*    | | This file is also tightly coupled to       | |
*    | | Impersonator's src/javascript/inputValues  | |
*  __| |____________________________________________| |__
* (__   ____________________________________________   __)
*    | |                                            | |
*/

import store from "./store";
import { getDevice } from "./device";
import getUserAgent from "./userAgent";
import getOperatingSystem from "./os";
import semverGt from "semver/functions/gt";
import semverLt from "semver/functions/lt";
import semverEq from "semver/functions/eq";
import semverNeq from "semver/functions/neq";
import semverGte from "semver/functions/gte";
import semverLte from "semver/functions/lte";

const evaluateRule = async (rule, userInfo) => {
    try {
        switch (rule.store) {
            case "byzantium":
                return evaluateUserInfo(rule, userInfo.byzantium);
            case "userInfo":
                return evaluateUserInfo(rule, userInfo);
            case "geo":
                return evaluateGeo(rule, userInfo);
            case "device":
                return evaluateDevice(rule);
            case "userAgent":
                return evaluateUserAgent(rule);
            case "os":
                return evaluateOperatingSystem(rule);
            case "contextInfo":
                return evaluateAdditionalInfo(rule, userInfo.contextInfo);
            case "tagsInfo":
                return evaluateAdditionalInfo(rule, userInfo.tagsInfo);
            case "contentMetadata":
                return evaluateAdditionalInfo(rule, userInfo.contentMetadata);
            case "url":
                return evaluateUrlInfo(rule, userInfo.urlUtmValues);
            case "semver":
                return evaluateSemVer(rule, userInfo.appVersion);
            default:
                return false;
        }
    } catch (err) {
        return false;
    }
};

/*
rule: {
  id: 'geoIP',
  comparator: 'eq',
  value: 'US'
}
*/
const evaluateGeo = async (rule, userInfo) => {
    const countryCode = (await store.geoIP(userInfo))?.country;

    return evaluate(countryCode, rule.value, rule.comparator, rule.type);
};

const evaluateDevice = async (rule) => {
    return evaluate(getDevice(), rule.value, rule.comparator, rule.type);
};

const evaluateUserAgent = async (rule) => {
    return evaluate(getUserAgent(), rule.value, rule.comparator, rule.type);
};

const evaluateOperatingSystem = async (rule) => {
    return evaluate(getOperatingSystem(), rule.value, rule.comparator, rule.type);
};

const evaluateInclusionConfidence = (rule, objects, ruleId) => {
    if (!objects) {
        return false;
    }

    const foundObject = objects.find((obj) => {
        return obj[`${ ruleId }Id`] === rule.id;
    });

    if (!foundObject) {
        return false;
    }

    return evaluate(foundObject.confidence, rule.value, rule.comparator, rule.type);
};

/*
rule: {
  id: 'segment',
  value: '9aISG2LscB8lVl9WA/74Lw==',
  comparator: 'eq'
}
*/
const evaluateInclusion = (rule, objects, ruleId) => {
    if (!objects) {
        return false;
    }

    const foundObject = objects.filter((object) => {
        if (typeof object !== "object") {
            // This is that we can have an array of strings.
            // The else block requires an actual array of objects.
            return evaluate(object, rule.value, rule.comparator, rule.type);
        } else {
            return object[`${ ruleId }Id`]
                ? evaluate(object[`${ ruleId }Id`], rule.value, rule.comparator, rule.type)
                : evaluate(object.id, rule.value, rule.comparator, rule.type);
        }
    });

    return rule.comparator === "not" ? foundObject.length === objects.length : foundObject.length === 1;
};

function evaluate(value, compareTo, comparator, ruleType) {
    let regex;

    if (ruleType === "boolean") {
        return evaluateBoolean(value, compareTo, comparator);
    }

    if (comparator !== "rgx" && (ruleType === "string" || ruleType === "enumeration")) {
        value = value?.toUpperCase?.() ?? value;
        compareTo = compareTo?.toUpperCase?.() ?? value;
    }

    if (value === undefined) {
        return false;
    }

    switch (comparator) {
        case "gt":
            return value > compareTo;
        case "lt":
            return value < compareTo;
        case "eq":
            return value === compareTo;
        case "not":
            return value !== compareTo;
        case "gte":
            return value >= compareTo;
        case "lte":
            return value <= compareTo;
        case "ct":
            return value.indexOf(compareTo) !== -1;
        case "nct":
            return value.indexOf(compareTo) === -1;
        case "rgx":
            regex = new RegExp(compareTo);

            return value.match(regex) !== null;
        default:
            return false;
    }
}

function evaluateBoolean(value, compareTo, comparator) {
    /*
        booleans need to evaluate falsy and truthy values
        even when "value" is undefined. per ENG4DTOOL-1474
    */
    switch (comparator) {
        case "eq":
            return value === compareTo;
        case "not":
            return value !== compareTo;
        default:
            return false;
    }
}

const evaluateUserInfo = (rule, userInfo) => {
    const paths = rule.path.split(".");
    const finalPath = paths[paths.length - 1];
    const prop = getProperty(paths, userInfo);

    if (Array.isArray(prop)) {
        return rule.type === "numeric"
            ? evaluateInclusionConfidence(rule, prop, finalPath)
            : evaluateInclusion(rule, prop, finalPath);
    } else {
        return evaluate(prop, rule.value, rule.comparator, rule.type);
    }
};

const evaluateAdditionalInfo = (rule, topLevelObject) => {
    const paths = rule.path.split(".");
    const prop = getProperty(paths, topLevelObject);

    if (Array.isArray(prop)) {
        //Currently the final part of the path is passed but this may need to change to the remainder of the path.
        return evaluateInclusion(rule, prop, paths[paths.length - 1]);
    }

    return evaluate(prop, rule.value, rule.comparator, rule.type);
};

export const evaluateUrlInfo = (rule, urlValues) => {
    switch (rule.id) {
        case "utmCode":
            return evaluateUtmCodes(rule, urlValues.utmValues);
        case "queryParameters":
            return evaluateAllQueryParmeters(rule, urlValues.queryParams);
        case "url":
            return evaluateAdditionalInfo(rule, urlValues);
    }
};

/**
 * @param {Object} rule - the rule specified by Abba
 * @param {URLSearchParams} utmQueryParams - the ones with keys starting with utm_
 * The only keys in the query parameters are ones that match utm_*.
 * If any of their values match the rule comparator, then return true. otherwise return false
 * (implicit OR test)
 */
const evaluateUtmCodes = (rule, utmQueryParams) => {
    return [...utmQueryParams.values()]
        .some((utmQueryParamValue) =>
            evaluate(utmQueryParamValue, rule.value, rule.comparator, rule.type)
        );
};

/**
 * @param {Object} rule - the rule specified by Abba
 * @param {URLSearchParams} queryParamInfo - all query params in the url
 * If any of their values match the rule indicator value then for those,
 * match against the rule value using the comparator and return true. otherwise return false
 * (implicit OR test)
 */
const evaluateAllQueryParmeters = (rule, queryParamInfo) => {
    return [...queryParamInfo.keys()]
        .filter((queryParameter) => evaluate(queryParameter, rule.indicator_value, rule.indicator_comparator, rule.type))
        .some((queryParamKey) => evaluate(queryParamInfo.get(queryParamKey), rule.value, rule.comparator, rule.type));
};

/**
 * Evaluates a semantic versioning string
 * @param {Object} rule - the rule specified by Abba
 * @param {string} semVerString - the semantic versioning info / string
 */
const evaluateSemVer = (rule, semVerString) => {
    switch (rule.comparator) {
        case "gt":
            return semverGt(semVerString, rule.value);
        case "lt":
            return semverLt(semVerString, rule.value);
        case "eq":
            return semverEq(semVerString, rule.value);
        case "not":
            return semverNeq(semVerString, rule.value);
        case "gte":
            return semverGte(semVerString, rule.value);
        case "lte":
            return semverLte(semVerString, rule.value);
        default:
            return false;
    }
};

const getProperty = (paths, clientValueObject) =>
    paths.reduce(
        (accumulator, pathValue) =>
            accumulator !== undefined && accumulator[pathValue] !== undefined ? accumulator[pathValue] : undefined,
        clientValueObject
    );

export default evaluateRule;
